"use client";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@nyntax/design-system/components/ui/pagination";
import { cn } from "@nyntax/design-system/lib/utils";
import { useMemo } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { useDataTable } from "./data-table-context";

interface DataTablePaginationProps {
  className?: string;
  siblingCount?: number;
}

function buildPageRange(
  currentPage: number,
  totalPages: number,
  siblingCount: number
): (number | "ellipsis")[] {
  const totalSlots = siblingCount * 2 + 5;

  if (totalPages <= totalSlots) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = siblingCount * 2 + 3;
    const leftRange = Array.from({ length: leftCount }, (_, i) => i + 1);
    return [...leftRange, "ellipsis", totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightCount = siblingCount * 2 + 3;
    const rightRange = Array.from(
      { length: rightCount },
      (_, i) => totalPages - rightCount + i + 1
    );
    return [1, "ellipsis", ...rightRange];
  }

  const middleRange = Array.from(
    { length: rightSibling - leftSibling + 1 },
    (_, i) => leftSibling + i
  );
  return [1, "ellipsis", ...middleRange, "ellipsis", totalPages];
}

function DataTablePagination({
  className,
  siblingCount = 1,
}: DataTablePaginationProps) {
  const table = useDataTable();
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const currentPage = pageIndex + 1;
  const canPrevious = table.getCanPreviousPage();
  const canNext = table.getCanNextPage();

  const pages = useMemo(
    () => buildPageRange(currentPage, pageCount, siblingCount),
    [currentPage, pageCount, siblingCount]
  );

  if (pageCount <= 1) {
    return null;
  }

  return (
    <Pagination className={cn("order-1 mx-0 w-fit md:order-2", className)}>
      <PaginationContent className="gap-1">
        <PaginationItem>
          <PaginationLink
            aria-disabled={!canPrevious}
            aria-label="Go to previous page"
            className={cn(!canPrevious && "pointer-events-none opacity-50")}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (canPrevious) {
                table.previousPage();
              }
            }}
          >
            <LuChevronLeft />
          </PaginationLink>
        </PaginationItem>

        {pages.map((page, idx) =>
          page === "ellipsis" ? (
            <PaginationItem key={`ellipsis-${idx === 1 ? "start" : "end"}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <PaginationLink
                href="#"
                isActive={page === currentPage}
                onClick={(e) => {
                  e.preventDefault();
                  table.setPageIndex(page - 1);
                }}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <PaginationLink
            aria-disabled={!canNext}
            aria-label="Go to next page"
            className={cn(!canNext && "pointer-events-none opacity-50")}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (canNext) {
                table.nextPage();
              }
            }}
          >
            <LuChevronRight />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

export type { DataTablePaginationProps };
export { DataTablePagination };
